import './settings.scss';
import React, {FormEvent, useState} from 'react';
import {batch, useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Form} from 'react-bootstrap';
import {changeSettings} from './duck/settings-actions';
import {fetchFacets, search, searchResetFacetsAction} from '../search/duck/search-actions';
import {useLoadVersionsRequest, useSearchPageType} from '../search/search-hooks';
import {ToggleSlider} from './toggleSlider';
import {CONTAINER_VERSION} from '../config';
import {MessageBox} from '../message-box/message-box';
import {MESSAGE_BOX_SETTINGS_ID} from './duck/settings-types';
import {useAuthContext} from '../auth/AuthContextProvider';
import {HibuTextButton} from '../common/hibuTextButton/HibuTextButton';

export const SettingsPage = React.memo((): JSX.Element => {
    const settingsConfig = useSelector((state) => state.config.settings);
    const {push} = useHistory();
    if (!settingsConfig.enabled) {
        push('/');
    }
    const [validated, setValidated] = useState(false);
    const settingsContainerName = useSelector((state) => state.settings.containerName);
    const settingsTechnicalDetails = useSelector((state) => state.settings.technicalDetails);
    const settingsManualValidation = useSelector((state) => state.settings.manualValidation);
    const settingsFieldSpecificSearch = useSelector((state) => state.settings.fieldSpecificSearch);
    const [indeksoVersion, setIndeksoVersion] = useState();
    const indeksoUi = CONTAINER_VERSION;
    const [indeksoUiBackendVersion, setIndeksoUiBackendVersion] = useState();
    const [containerName, setContainerName] = useState(settingsContainerName);
    const [showTechnicalDetails, setShowTechnicalDetails] = useState(settingsTechnicalDetails);
    const [showManualValidation, setShowManualValidation] = useState(settingsManualValidation);
    const [showFieldSpecificSearch, setShowFieldSpecificSearch] = useState(settingsFieldSpecificSearch);
    const dispatch = useDispatch();
    const searchPageType = useSearchPageType();
    const history = useHistory();
    const {t} = useTranslation();
    const {userId} = useAuthContext();

    const containers = useSelector((state) => state.settings.containers);
    const extractVersions = (versionResponse) => {
        setIndeksoVersion(versionResponse['indekso.fullVersion']);
        setIndeksoUiBackendVersion(versionResponse['hibu-ui-backend.version']);
    };

    useLoadVersionsRequest(extractVersions);

    const onGoBack = () => {
        history.goBack();
    };

    const getContent = (): JSX.Element => {
        const onSubmit = (event: FormEvent) => {
            event.preventDefault();
            if (!validated) {
                event.stopPropagation();
            }
            setValidated(true);
            batch(() => {
                dispatch(
                    changeSettings(
                        containerName,
                        showTechnicalDetails,
                        showManualValidation,
                        showFieldSpecificSearch,
                    )
                );
                dispatch(searchResetFacetsAction(searchPageType));
                dispatch(fetchFacets(searchPageType));
                dispatch(search(searchPageType, history));
            });
            history.replace();
        };

        const configToggles = settingsConfig.toggles;
        return (
            <div>
                <Form validated={validated} onSubmit={onSubmit}>
                    {configToggles.showUserId &&
                        <>
                            <h1 className="form-heading">{t('settings.title')}</h1>
                            <Form.Group>
                                <Form.Label>{t('settings.userId')}</Form.Label>
                                <div className={'setting'}>
                                    <div>{userId}</div>
                                </div>
                            </Form.Group>
                        </>
                    }
                    <Form.Group>
                        {configToggles.showContainerName &&
                            <>
                                <Form.Label>{t('settings.containerName')}</Form.Label>
                                <div className={'setting'}>
                                    <Form.Control
                                        required
                                        as="select"
                                        className={'form-control'}
                                        custom
                                        onChange={(e) => setContainerName(e.target.value)}
                                        value={containerName}
                                    >
                                        {Object.values(containers).map((container) => (
                                            <option key={container.name} value={container.name}>
                                                {container.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </div>
                            </>
                        }
                    </Form.Group>
                    <Form.Group>
                        {(configToggles.showTechnicalDetails || configToggles.showManualValidation) &&
                            <Form.Label>{t('settings.display')}</Form.Label>
                        }
                        {configToggles.showTechnicalDetails &&
                            <div className={'setting'}>
                                <Form.Label>{t('settings.technicalDetails')}</Form.Label>
                                <ToggleSlider
                                    name="technicalDetails"
                                    checked={showTechnicalDetails}
                                    onChange={(e) => setShowTechnicalDetails(e.target.checked)}
                                />
                            </div>
                        }
                        {configToggles.showManualValidation &&
                            <div className={'setting'}>
                                <Form.Label>{t('settings.manualValidation')}</Form.Label>
                                <ToggleSlider
                                    name="manualValidation"
                                    checked={showManualValidation}
                                    onChange={(e) => setShowManualValidation(e.target.checked)}
                                />
                            </div>
                        }
                    </Form.Group>
                    <Form.Group>
                        {(configToggles.showFieldSpecificSearch) &&
                            <Form.Label>{t('settings.search')}</Form.Label>
                        }
                        {configToggles.showFieldSpecificSearch &&
                            <div className={'setting'}>
                                <Form.Label>{t('settings.fieldSpecificSearch')}</Form.Label>
                                <ToggleSlider
                                    name="fieldSpecificSearch"
                                    checked={showFieldSpecificSearch}
                                    onChange={(e) => setShowFieldSpecificSearch(e.target.checked)}
                                />
                            </div>
                        }
                    </Form.Group>
                    <div className={'settings-form-buttons'}>
                        <HibuTextButton disabled={false} label={t('general.cancel')} type="reset" onClick={onGoBack}/>
                        <HibuTextButton disabled={false} label={t('settings.submitButton')} filled={true}
                                        type="submit"/>
                    </div>
                    <div className={'settings-footer'}>
                        <div>hibu-ui : {indeksoUi}</div>
                        <div>hibu-ui-backend : {indeksoUiBackendVersion}</div>
                        <div>hibu : {indeksoVersion}</div>
                    </div>
                </Form>
            </div>
        );
    };
    return (
        <div id="settings-component">
            <MessageBox id={MESSAGE_BOX_SETTINGS_ID}/>
            {getContent()}
        </div>
    );
});
